import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage/session';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import ReferenceReducer from './referenceData.slice';
import FullPolicyReducer from './fullPolicy.slice';
import PolicyDocumentReducer from './policyDocuments.slice';
import PayNowReducer from './paynow.slice';
import MidTermAdjustmentReducer from './midTermAdjustment.slice';
import MidTermAdjustmentRatingResponseReducer from './midTermAdjustmentRatingResponse.slice';
import vehicleLookupReducer from './vehicleLookup.slice';
import CardPaymentReducer from './cardPaymentSlice';
import midTermAdjustmentAPIReducer from './midTermAdjustmentAPI.sclice';
import renewalChangesReducer from './renewalChanges.slice';
import renewalReducer from './renewal.slice';
import ManagedPaymentsReducer from './managedPayments.slice';
import ContactPreferencesSlice from './contactPreferences.slice';
import subscriptionReducer from './subscription.slice';
import subscriptionAmendDriversReducer from './subscription-amend-drivers.slice';
import subscriptionChangesReducer from './subscriptionChanges.slice';
import takeupReducer from './RestartTakeUp.slice';
import cancellationReducer from './cancellation/cancellation.slice';
import telematicsTripsReducer from './telematicsTripsSlice';
import telematicsTripDetailsReducer from './telematicsTripDetailsSlice';
import telematicsDriverScoreReducer from './telematicsDriverScoreSlice';
import telematicsEnabledPolicyReducer from './telematicsEnabledPolicySlice';
import telematicsDriverMileageReducer from './telematicsDriverMileageSlice';
import telematicsTopUpMilesReducer from './telematicsTopUpMilesSlice';
import driverMileageReducer from './driverMileageSlice';

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
	stateTransformer: (state: RootState) => {
		const transformedState = {
			...state,
			referenceData: state.referenceData.licenceTypes.length > 0 ? 'Successfully Loaded' : 'Failed to Load',
		};
		return transformedState;
	},
});

const persistConfig = {
	key: 'root',
	version: 1,
	storage,
};

const rootReducer = combineReducers({
	referenceData: ReferenceReducer,
	fullPolicy: FullPolicyReducer,
	policyDocuments: PolicyDocumentReducer,
	payNow: PayNowReducer,
	midTermAdjustment: MidTermAdjustmentReducer,
	midTermAdjustmentResponse: MidTermAdjustmentRatingResponseReducer,
	vehicleLookup: vehicleLookupReducer,
	midTermAdjustmentAPI: midTermAdjustmentAPIReducer,
	cardPayment: CardPaymentReducer,
	renewalChanges: renewalChangesReducer,
	renewal: renewalReducer,
	managedPayments: ManagedPaymentsReducer,
	contactPreferences: ContactPreferencesSlice,
	subscription: subscriptionReducer,
	subscriptionAmendDrivers: subscriptionAmendDriversReducer,
	subscriptionChangesReducer: subscriptionChangesReducer,
	takeUpDetails: takeupReducer,
	cancellation: cancellationReducer,
	telematicsTrips: telematicsTripsReducer,
	telematicsTripDetails: telematicsTripDetailsReducer,
	telematicsDriverScore: telematicsDriverScoreReducer,
	telematicsEnabledPolicy: telematicsEnabledPolicyReducer,
	telematicsDriverMileage: telematicsDriverMileageReducer,
	telematicsTopUpMiles: telematicsTopUpMilesReducer,
	driverMileage: driverMileageReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
	reducer: persistedReducer,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
			},
		}),
	enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer),
});

export function setupStore(preloadedState?: Partial<RootState>) {
	return configureStore({
		reducer: rootReducer,
		preloadedState,
	});
}

export const persistor = persistStore(store);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
export type AppStore = ReturnType<typeof setupStore>;
